import { useT, useViewDataSource } from '@powerportalspro/react';
import { QueryAggregate, ChartLegendPosition, ChartType } from '@powerportalspro/react-charts';
import {
  DataverseChart,
  GridToolbarItems,
  MainGrid,
  NavigateOpenRecordGridButton,
  Section,
  SectionColumn,
  type ViewDefinition,
} from '@powerportalspro/react-fluent';
import { useMemo } from 'react';

// A pie chart and a grid sharing one data source (useViewDataSource): one request brings back both
// the page of accounts the grid shows and the count of accounts in each industry the chart draws,
// so the two never disagree. Click a slice to highlight its industry's accounts in the grid — no
// second request — and click it again to clear it. Searching the grid re-counts the slices over
// the accounts it now matches.

const STRINGS_BASE = 'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.ChartDemos.PieChartShowcaseMainDemo';

const ACCOUNT_URL = 'demos/account';

const ACCOUNTS_VIEW_ID = '6f1d2c3b-4a59-4e68-9c7d-8e9fa0b1c2d3';

// The grid shows each account's industry, so a highlighted slice has rows to match.
const ACCOUNTS_FETCH_XML = `<fetch>
  <entity name='account'>
    <attribute name='name' />
    <attribute name='industrycode' />
    <attribute name='address1_city' />
    <attribute name='revenue' />
    <order attribute='name' />
  </entity>
</fetch>`;

export function PieChartShowcaseMainSample() {
  const t = useT();

  // The view exists only here, not in Dataverse, so the data source gets its FetchXML to run, and
  // its id, which the server uses to find the view's localized column titles.
  const accounts = useViewDataSource({
    viewId: ACCOUNTS_VIEW_ID,
    fetchXml: ACCOUNTS_FETCH_XML,
    aggregations: [
      {
        groupBy: { column: 'industrycode' },
        aggregateColumn: 'accountid',
        aggregate: QueryAggregate.Count,
      },
    ],
  });

  const views = useMemo<ViewDefinition[]>(
    () => [
      {
        id: ACCOUNTS_VIEW_ID,
        tableName: 'account',
        displayName: t(`${STRINGS_BASE}.view-accounts`),
        fetchXml: ACCOUNTS_FETCH_XML,
        columns: [
          { columnName: 'name', width: 220 },
          { columnName: 'industrycode', width: 200 },
          { columnName: 'address1_city', width: 150 },
          { columnName: 'revenue', width: 150 },
        ],
      },
    ],
    [t],
  );

  return (
    <Section borderVisible={false}>
      <SectionColumn width={2}>
        <DataverseChart
          dataSource={accounts}
          type={ChartType.Pie}
          title={t(`${STRINGS_BASE}.chart-title`)}
          legendPosition={ChartLegendPosition.Bottom}
          height="450px"
        />
      </SectionColumn>
      <SectionColumn width={3}>
        <MainGrid
          dataSource={accounts}
          tableName="account"
          viewIds={[ACCOUNTS_VIEW_ID]}
          customViewDefinitions={views}
          defaultViewId={ACCOUNTS_VIEW_ID}
          minHeight="450px"
          maxHeight="450px"
        >
          <GridToolbarItems>
            <NavigateOpenRecordGridButton urlFor={(record) => `${ACCOUNT_URL}?accountId=${record.id}`} />
          </GridToolbarItems>
        </MainGrid>
      </SectionColumn>
    </Section>
  );
}
